/**
 * Document Hub — filtering, sorting, pagination, view toggle and the request
 * dialog.
 *
 * The LIST markup is the source of truth. Every document is rendered
 * server-side there, and the server paints that view, so a dead bundle still
 * leaves every document the query string asked for visible and downloadable.
 * TILES is the default view all the same (October 2026 review): the first
 * apply() switches to it, under the page loader.
 * The controls that only make sense with scripting (pagination, the view
 * toggle, the mobile filter toggle) ship hidden and are revealed here rather
 * than rendering as a page of controls that do nothing.
 *
 * The TILES view is built here, from those list rows, the first time it is
 * shown -- now on every visit, since it is the default. It used to be rendered server-side too, on the reasoning that a
 * module which only ever hides, shows and reorders existing nodes can never
 * trip the .reveal trap — app.js initialises .reveal once at DOMContentLoaded,
 * and under .awdd-anim a .reveal element created later stays
 * visibility:hidden forever, silently. That trap is real, but the price was
 * 290 KB and 1,477 elements of hidden duplicate markup on every visit, over
 * half the document, for a view most visitors never open. The answer is the
 * one project-archive already uses for its own JS-built tiles, and it is
 * structural rather than careful: the card carries no .reveal class, so there
 * is nothing for the gate to hide. Keep it that way.
 *
 * What is NOT duplicated here: the card's markup and class strings live in the
 * block template as a <template>, and the tile control's skin comes from
 * awdd_document_tile_ui() through a data attribute. A tile's control is a
 * clone of its row's <a>, so href, target, rel, aria-label and the data-doc-*
 * attributes are whatever PHP wrote — there is no second copy of that logic to
 * drift, which is the same reason productGroups is passed in as data.
 */

import { icon } from './icons.js';

export function initDocumentHub() {
    document.querySelectorAll('[data-doc-hub]').forEach(root => {
        const results = root.querySelector('[data-doc-results]');
        if (!results) return;

        // The block's per_page is the default; the visitor's Show select can
        // change it, where 0 means "All" and is held as Infinity so the paging
        // maths below needs no special case.
        let perPage = Math.max(1, parseInt(root.dataset.docPerPage, 10) || 12);
        const views = {
            tiles: root.querySelector('[data-doc-view="tiles"]'),
            list: root.querySelector('[data-doc-view="list"]'),
        };
        const items = view => Array.from(views[view]?.querySelectorAll('[data-doc-item]') || []);

        // ---- the tiles view, stamped from the list rows on first use --------
        const tileTemplate = root.querySelector('[data-doc-tile-template]');

        let catIcons = {};
        let tileUi = {};
        try { catIcons = JSON.parse(root.dataset.docCatIcons || '{}'); } catch (e) { catIcons = {}; }
        try { tileUi = JSON.parse(root.dataset.docTileUi || '{}'); } catch (e) { tileUi = {}; }

        const slot = (card, name) => card.querySelector(`[data-doc-slot="${name}"]`);

        // A slot is hidden in the template and revealed only when it has
        // something to show, mirroring the `if ($doc['x'])` guards the
        // server-rendered card used to carry. Without that an absent type or
        // size would paint an empty badge rather than nothing.
        const fill = (card, name, value, html) => {
            const el = slot(card, name);
            if (!el) return;
            if (!value) { el.hidden = true; return; }
            if (html) el.innerHTML = value; else el.textContent = value;
            el.hidden = false;
        };

        const buildTile = row => {
            const card = tileTemplate.content.firstElementChild.cloneNode(true);

            // Carry every data-* the filter, sort and count code reads. Copied
            // rather than re-derived so the two views cannot disagree about
            // what a document is.
            ['cat', 'product', 'joint', 'downloads', 'search', 'title', 'type'].forEach(key => {
                card.dataset[key] = row.dataset[key] || '';
            });

            const img = slot(card, 'image');
            if (img && row.dataset.img) {
                img.src = row.dataset.img;
                img.hidden = false;
            }

            fill(card, 'icon', catIcons[row.dataset.cat] || '', true);
            fill(card, 'type', row.dataset.type);
            fill(card, 'title', row.dataset.title);
            // 'All' is the catch-all product term and reads as noise on
            // a card, exactly as the server-rendered tile decided.
            fill(card, 'product', row.dataset.product && row.dataset.product !== 'All' ? row.dataset.product : '');
            fill(card, 'updated', row.dataset.updated ? `Updated ${row.dataset.updated}` : '');
            fill(card, 'size', row.dataset.size);

            const action = row.querySelector('a');
            if (action) {
                const clone = action.cloneNode(true);
                const isRequest = clone.hasAttribute('data-doc-request');
                clone.className = isRequest ? tileUi.requestClass || '' : tileUi.downloadClass || '';
                if (isRequest) {
                    clone.textContent = tileUi.requestLabel || 'Request document';
                } else {
                    clone.innerHTML = tileUi.downloadLabel || 'Download';
                }
                slot(card, 'action')?.appendChild(clone);
            }

            return card;
        };

        let tilesBuilt = false;
        const ensureTiles = () => {
            if (tilesBuilt || !tileTemplate || !views.tiles) return;
            tilesBuilt = true;
            const frag = document.createDocumentFragment();
            items('list').forEach(row => frag.appendChild(buildTile(row)));
            views.tiles.appendChild(frag);
        };

        const countEl = root.querySelector('[data-doc-count]');
        const scopeEl = root.querySelector('[data-doc-scope]');
        const emptyEl = root.querySelector('[data-doc-empty]');
        const pager = root.querySelector('[data-doc-pagination]');
        const sortEl = root.querySelector('[data-doc-sort]');
        const searchInput = root.querySelector('[data-doc-search-input]');
        const searchForm = root.querySelector('[data-doc-search-form]');
        const clearEl = root.querySelector('[data-doc-clear]');
        const filtersWrap = root.querySelector('[data-doc-filters]');
        const filtersToggle = root.querySelector('[data-doc-filters-toggle]');
        const viewToggle = root.querySelector('[data-doc-view-toggle]');

        // Which document `product` values each filter option covers. PHP owns
        // this map and emits it on the block root; this used to be a second,
        // hand-kept copy of it, and the two had to stay byte-identical or the
        // server-rendered first paint disagreed with the first interaction.
        // Falling back to {} rather than to a literal is deliberate: a literal
        // would silently diverge again, whereas {} makes a missing attribute
        // obvious (the product filters match nothing) instead of subtly wrong.
        let productGroups = {};
        try {
            productGroups = JSON.parse(root.dataset.docProductGroups || '{}');
        } catch (e) {
            productGroups = {};
        }

        // The same arrangement for the five category filters: PHP owns the
        // label => category-slug map, drops any slug whose term no longer
        // exists, and emits what survives. Falling back to {} for the same
        // reason as above.
        let catGroups = {};
        try {
            catGroups = JSON.parse(root.dataset.docCatGroups || '{}');
        } catch (e) {
            catGroups = {};
        }

        const initialParams = new URLSearchParams(location.search);
        // The page arrived carrying a deep link (Home's link-panel tiles, or a
        // header search) only when one of these keys is present at all — not
        // when it merely resolves to an empty string. Read before state.cat gets
        // normalised below, so an invalid ?cat= still counts as "arrived filtered"
        // for the purposes of the scroll-to-results below.
        const arrivedFiltered = initialParams.has('cat') || initialParams.has('q');

        const state = {
            cat: initialParams.get('cat') || '',
            query: initialParams.get('q') || '',
            products: [],
            joints: [],
            // The five category checkboxes, held as labels the way `products`
            // is. Separate from `cat` above, which is the single-valued deep
            // link the hero pills and Home's shortcuts set: a visitor can tick
            // several of these at once, and the two are ANDed.
            cats: [],
            sort: 'az',
            // Tiles: the client first asked for list (content brief, Document
            // Hub #7), then switched to tiles in the October 2026 review. This
            // must agree with the toggle button the template marks
            // aria-pressed. The template still paints the LIST container
            // visible, as the no-JS fallback; the first apply() below hides it
            // and builds the tiles.
            view: 'tiles',
            page: 1,
        };

        // The template falls an unknown ?cat= back to "" (all documents) — see
        // the comment beside awdd_document_matches() in the block template. The
        // JS took the param verbatim, so a category renamed in wp-admin (its
        // slug changes, but Home's seeded ?cat= links don't) made every tile's
        // dataset.cat disagree with state.cat: matches() rejected all 38 rows,
        // the count read 0 and the empty state took over a page that had just
        // painted every document. The valid slugs are already in the DOM as the
        // hero pills' data-doc-cat values (data-doc-cat="" is the "All" pill),
        // so there is no second source of truth to keep in sync.
        const validCats = new Set(
            Array.from(root.querySelectorAll('[data-doc-cat]')).map(btn => btn.dataset.docCat)
        );
        if (!validCats.has(state.cat)) state.cat = '';

        // A document's joints, from the space-separated data-joint list.
        const jointsOf = el => (el.dataset.joint || '').split(' ').filter(Boolean);

        // Mirrors awdd_document_matches() in the block template. The two must
        // agree or the first paint disagrees with the first interaction. Both
        // sides trim the query at the point of comparison — see the comment
        // beside $awdd_dh_query in the block template.
        const matches = el => {
            if (state.cat && el.dataset.cat !== state.cat) return false;
            if (state.query && !el.dataset.search.includes(state.query.trim().toLowerCase())) return false;
            if (state.products.length) {
                const vals = state.products.flatMap(p => productGroups[p] || []);
                if (!vals.includes(el.dataset.product)) return false;
            }
            // data-joint is a space-separated list: one comparison brochure
            // covers two joints, so it has to match either box.
            if (state.joints.length && !state.joints.some(j => jointsOf(el).includes(j))) return false;
            if (state.cats.length) {
                const slugs = state.cats.map(c => catGroups[c]).filter(Boolean);
                if (!slugs.includes(el.dataset.cat)) return false;
            }
            return true;
        };

        const sorters = {
            az: (a, b) => a.dataset.title.localeCompare(b.dataset.title),
            za: (a, b) => b.dataset.title.localeCompare(a.dataset.title),
            type: (a, b) =>
                a.dataset.type.localeCompare(b.dataset.type) ||
                a.dataset.title.localeCompare(b.dataset.title),
            // Real download counts (inc/document-downloads.php), most first;
            // ties, including every never-downloaded document, fall back to A–Z.
            popular: (a, b) =>
                (parseInt(b.dataset.downloads, 10) || 0) - (parseInt(a.dataset.downloads, 10) || 0) ||
                a.dataset.title.localeCompare(b.dataset.title),
        };

        const writeUrl = () => {
            const params = new URLSearchParams(location.search);
            // ?cat= carries a single category, so it can represent state.cat or
            // exactly one ticked category checkbox, but not several. The second
            // source keeps a single ticked rail category in the address bar, so
            // the link can be shared; it reopens as that category's pill.
            const urlCat = state.cat || (1 === state.cats.length ? catGroups[state.cats[0]] || '' : '');
            urlCat ? params.set('cat', urlCat) : params.delete('cat');
            state.query ? params.set('q', state.query) : params.delete('q');
            const qs = params.toString();
            history.replaceState(null, '', qs ? `${location.pathname}?${qs}` : location.pathname);
        };

        const renderPager = total => {
            const pages = Math.max(1, Math.ceil(total / perPage));
            pager.innerHTML = '';
            if (pages < 2) { pager.hidden = true; return; }
            pager.hidden = false;

            const goto = n => {
                state.page = n;
                apply();
                root.querySelector('#results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            };

            // Round arrow button on each end, matching DocumentHub.jsx:389-414 and
            // the server-rendered pager in inc/pagination.php. Unlike that one these
            // ARE buttons, so the reference's disabled: variants port across — on
            // the first and last page the arrow stays in place, dimmed and inert,
            // rather than disappearing and shifting the row.
            //
            // These are built after DOMContentLoaded, so no .reveal class: under
            // .awdd-anim a reveal added later is hidden by CSS forever, with the
            // GSAP tween that would un-hide it already initialised and gone.
            const arrow = (label, target, rotate) => {
                const b = document.createElement('button');
                b.type = 'button';
                b.className = 'inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink transition-colors hover:border-ink disabled:cursor-not-allowed disabled:opacity-40';
                b.setAttribute('aria-label', label);
                b.disabled = target < 1 || target > pages;
                b.innerHTML = icon('arrow', rotate ? 'h-[14px] rotate-180' : 'h-[14px]');
                b.addEventListener('click', () => { if (!b.disabled) goto(target); });
                return b;
            };

            pager.appendChild(arrow('Previous page', state.page - 1, true));
            for (let n = 1; n <= pages; n += 1) {
                const b = document.createElement('button');
                b.type = 'button';
                b.textContent = String(n);
                b.className =
                    n === state.page
                        ? 'h-9 min-w-[36px] rounded-full bg-steel-500 px-3 font-sans text-[13px] font-bold text-white transition-colors'
                        : 'h-9 min-w-[36px] rounded-full border border-line px-3 font-sans text-[13px] font-bold text-ink transition-colors hover:border-ink';
                b.setAttribute('aria-current', n === state.page ? 'page' : 'false');
                b.addEventListener('click', () => goto(n));
                pager.appendChild(b);
            }
            pager.appendChild(arrow('Next page', state.page + 1, false));
        };

        // Counted off the LIST rows, which are always in the document. This
        // used to read items('tiles'), which was equivalent only while both
        // views were rendered server-side; now the tiles may not exist yet, and
        // every filter count would read 0 until someone opened that view.
        const updateCounts = () => {
            const base = items('list').filter(el => {
                if (state.cat && el.dataset.cat !== state.cat) return false;
                if (state.query && !el.dataset.search.includes(state.query.trim().toLowerCase())) return false;
                return true;
            });
            root.querySelectorAll('[data-doc-count-product]').forEach(el => {
                const vals = productGroups[el.dataset.docCountProduct] || [];
                el.textContent = String(base.filter(d => vals.includes(d.dataset.product)).length);
            });
            root.querySelectorAll('[data-doc-count-joint]').forEach(el => {
                el.textContent = String(base.filter(d => jointsOf(d).includes(el.dataset.docCountJoint)).length);
            });
            root.querySelectorAll('[data-doc-count-cat]').forEach(el => {
                const slug = catGroups[el.dataset.docCountCat];
                el.textContent = String(base.filter(d => d.dataset.cat === slug).length);
            });
        };

        const apply = () => {
            // Built before anything reads items('tiles'), and only ever once.
            if ('tiles' === state.view) ensureTiles();

            // From the list rows, which always exist. It used to be assigned
            // inside the loop below and so belonged to whichever view ran last
            // — which happened to be the list, and happened to be right only
            // because both views held the same documents.
            let total = items('list').filter(matches).length;

            Object.keys(views).forEach(view => {
                const container = views[view];
                if (!container) return;
                container.hidden = view !== state.view;

                const list = items(view);
                const visible = list.filter(matches).sort(sorters[state.sort] || sorters.az);

                // Reorder in place. Appending an existing node moves it; it is
                // never recreated, so its .reveal state survives.
                visible.forEach(el => container.appendChild(el));

                // Guarded: with "All", perPage is Infinity and 0 * Infinity is NaN.
                const start = Number.isFinite(perPage) ? (state.page - 1) * perPage : 0;
                list.forEach(el => { el.hidden = true; });
                visible.slice(start, start + perPage).forEach(el => { el.hidden = false; });
            });

            // "N documents in <category>" — the noun lives inside the bold half,
            // matching the reference (DocumentHub.jsx:276-277). Pluralised, which
            // the reference is not: a one-document category would read
            // "1 documents". The server prints the same two halves on first paint,
            // so blocks/document-hub/template.php has to agree with this.
            if (countEl) countEl.textContent = `${total} ${total === 1 ? 'document' : 'documents'}`;
            if (scopeEl) {
                // The scope follows the CATEGORY pills only, never the product or
                // joint checkboxes — that is what the reference's activeCatLabel
                // reads. The "all" wording comes from the server via a data
                // attribute rather than being repeated here, the same reason
                // productGroups is passed in as data rather than duplicated.
                const activePill = state.cat ? root.querySelector(`[data-doc-cat="${CSS.escape(state.cat)}"]`) : null;
                scopeEl.textContent = activePill
                    ? activePill.textContent.trim()
                    : (scopeEl.dataset.docScopeAll || 'all documents');
            }
            if (emptyEl) emptyEl.hidden = total !== 0;
            renderPager(total);
            updateCounts();
            writeUrl();

            root.querySelectorAll('[data-doc-cat]').forEach(btn => {
                btn.setAttribute('aria-pressed', btn.dataset.docCat === state.cat ? 'true' : 'false');
            });
        };

        const reset = () => { state.page = 1; apply(); };

        root.querySelectorAll('[data-doc-cat]').forEach(btn => {
            btn.addEventListener('click', () => {
                state.cat = btn.dataset.docCat === state.cat ? '' : btn.dataset.docCat;
                reset();
                root.querySelector('#results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        });

        root.querySelectorAll('[data-doc-product]').forEach(cb => {
            cb.addEventListener('change', () => {
                state.products = Array.from(root.querySelectorAll('[data-doc-product]:checked')).map(x => x.value);
                reset();
            });
        });

        root.querySelectorAll('[data-doc-joint]').forEach(cb => {
            cb.addEventListener('change', () => {
                state.joints = Array.from(root.querySelectorAll('[data-doc-joint]:checked')).map(x => x.value);
                reset();
            });
        });

        root.querySelectorAll('[data-doc-cat-filter]').forEach(cb => {
            cb.addEventListener('change', () => {
                state.cats = Array.from(root.querySelectorAll('[data-doc-cat-filter]:checked')).map(x => x.value);
                reset();
            });
        });

        // A deep link (Home's shortcuts, a hero pill) arrives as ?cat= and stays
        // on state.cat, so its pill lights up and the next pill click replaces
        // it. It used to be handed over to the matching rail checkbox instead,
        // and that broke the page (October 2026 review): the next pill click
        // was then ANDed with the hidden checkbox -- arrive on ?cat=tech, click
        // Coatings & Linings, and no document is in both, so the page went
        // empty with no pill lit to explain why.

        if (searchInput) {
            searchInput.addEventListener('input', () => { state.query = searchInput.value; reset(); });
        }
        if (searchForm) {
            searchForm.addEventListener('submit', e => {
                e.preventDefault();
                root.querySelector('#results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        }
        if (sortEl) {
            sortEl.addEventListener('change', () => { state.sort = sortEl.value; reset(); });
        }
        const showWrap = root.querySelector('[data-doc-show]');
        const showEl = root.querySelector('[data-doc-show-select]');
        if (showWrap && showEl) {
            showWrap.hidden = false;
            showEl.addEventListener('change', () => {
                const n = parseInt(showEl.value, 10);
                perPage = n > 0 ? n : Infinity;
                reset();
            });
        }
        if (clearEl) {
            clearEl.addEventListener('click', e => {
                e.preventDefault();
                state.cat = '';
                state.query = '';
                state.products = [];
                state.joints = [];
                state.cats = [];
                if (searchInput) searchInput.value = '';
                root.querySelectorAll('[data-doc-product], [data-doc-joint], [data-doc-cat-filter]').forEach(cb => { cb.checked = false; });
                reset();
            });
        }

        if (viewToggle) {
            viewToggle.hidden = false;
            viewToggle.querySelectorAll('[data-doc-view-btn]').forEach(btn => {
                btn.addEventListener('click', () => {
                    state.view = btn.dataset.docViewBtn;
                    viewToggle.querySelectorAll('[data-doc-view-btn]').forEach(b => {
                        const on = b === btn;
                        b.setAttribute('aria-pressed', on ? 'true' : 'false');
                        b.className = on
                            ? 'flex h-8 w-9 items-center justify-center bg-steel-500 text-white transition-colors'
                            : 'flex h-8 w-9 items-center justify-center text-mute2 transition-colors hover:text-ink';
                    });
                    // The note points at the other view, so it flips with this one.
                    const note = viewToggle.querySelector('[data-doc-view-note]');
                    if (note) note.textContent = state.view === 'list' ? note.dataset.docViewNoteList : note.dataset.docViewNoteTiles;
                    apply();
                });
            });
        }

        if (filtersToggle && filtersWrap) {
            filtersToggle.hidden = false;
            filtersWrap.classList.add('hidden', 'lg:flex');
            filtersToggle.addEventListener('click', () => {
                const open = filtersWrap.classList.toggle('hidden');
                filtersToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
            });
        }

        // Request dialog. The trigger is a real link to /contact in the markup,
        // so with a dead bundle a request-only document still leads somewhere;
        // here it is upgraded into a dialog opener.
        const dialog = root.querySelector('[data-doc-dialog]');
        if (dialog && typeof dialog.showModal === 'function') {
            const titleEl = dialog.querySelector('[data-doc-dialog-title]');

            // The form inside is an AJAX Gravity Form, and Gravity Forms
            // REPLACES its markup on every send: with the "Thanks" message on
            // success, with a fresh copy of the form on a validation error.
            // Two things used to break on that, both found in the October 2026
            // review (row 24):
            //  - the hidden Document field was looked up once, here, so after a
            //    failed send it pointed at a node no longer in the page, and the
            //    next document's name went nowhere: the client would have
            //    received a request naming the PREVIOUS document. It is now
            //    found afresh each time the dialog opens. Found by class, not
            //    field id, so editing the form in wp-admin cannot silently break
            //    the prefill.
            //  - after a successful send the dialog held only the "Thanks"
            //    message, so a second document could not be requested without
            //    reloading. The untouched form is cloned at load and put back
            //    when the dialog next opens. Gravity Forms' AJAX listener lives
            //    on its hidden iframe, outside the replaced markup, so the
            //    restored form submits exactly as the original did.
            const docField = () => dialog.querySelector('.awdd-doc-title input');
            const formWrap = dialog.querySelector('.gform_wrapper');
            const formId = formWrap ? (formWrap.id.match(/\d+$/) || [])[0] : null;
            const pristine = formWrap ? formWrap.cloneNode(true) : null;
            const RELOAD_KEY = 'awdd-doc-request';

            const freshForm = () => {
                const done = formId && dialog.querySelector(`#gform_confirmation_wrapper_${formId}`);
                if (!done) return true;
                if (!pristine) return false;
                done.replaceWith(pristine.cloneNode(true));
                // Gravity Forms' double-submit guard is set on send and only
                // cleared when it re-renders a form, which a confirmation is not.
                window[`gf_submitting_${formId}`] = false;
                if (window.jQuery) window.jQuery(document).trigger('gform_post_render', [Number(formId), 1]);
                return true;
            };

            const openFor = title => {
                if (!freshForm()) {
                    // No clean copy to restore: reload, and reopen for the same
                    // document, rather than leave the visitor at a dead end.
                    try { sessionStorage.setItem(RELOAD_KEY, title); } catch (err) { /* private mode: plain reload */ }
                    location.reload();
                    return;
                }
                if (titleEl) titleEl.textContent = title;
                const field = docField();
                if (field) field.value = title;
                dialog.showModal();
            };

            // Delegated from the block root rather than bound to each trigger
            // at init. A tile's control is cloned from its row's <a> when the
            // tiles view is first opened, and a listener bound before that
            // clone existed would never reach it — the tile would fall through
            // to its href and navigate to /contact instead of opening the
            // dialog. Degrading rather than breaking, but not what it should
            // do, and delegation removes the ordering question entirely.
            root.addEventListener('click', e => {
                const trigger = e.target.closest('[data-doc-request]');
                if (!trigger || !root.contains(trigger)) return;
                e.preventDefault();
                openFor(trigger.dataset.docTitle || '');
            });

            // Back from the reload fallback above: reopen for that document.
            try {
                const pending = sessionStorage.getItem(RELOAD_KEY);
                if (pending) {
                    sessionStorage.removeItem(RELOAD_KEY);
                    openFor(pending);
                }
            } catch (err) { /* storage blocked: nothing to resume */ }

            dialog.querySelector('[data-doc-dialog-close]')?.addEventListener('click', () => dialog.close());
            // Clicking the backdrop closes: the dialog element itself covers only
            // its own box, so a click landing on <dialog> is a click outside the
            // content panel.
            dialog.addEventListener('click', e => { if (e.target === dialog) dialog.close(); });
        }

        root.setAttribute('data-doc-hub-ready', '');
        apply();

        // Mirrors DocumentHub.jsx's arrival effect: a deep link from Home's
        // link-panel tiles or a header search should land the visitor on the
        // results it filtered for, not at the top of the hero. Only fires when
        // the page actually arrived with ?cat= or ?q= — a bare /document-hub/
        // load must never auto-scroll.
        // Deferred until after load, and then a frame further. At DOM-ready the
        // hero's watermark and the tiles' images have not sized yet, so #results
        // sits near the top of a page that is still short — scrollIntoView then
        // computes a target of roughly zero and silently does nothing. It reads
        // as though the call never fired. Verified in a browser: the identical
        // call lands correctly once layout has settled.
        if (arrivedFiltered) {
            const toResults = () =>
                requestAnimationFrame(() =>
                    root.querySelector('#results')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                );

            if (document.readyState === 'complete') {
                toResults();
            } else {
                window.addEventListener('load', toResults, { once: true });
            }
        }
    });
}
